import { ComponentType, lazy, LazyExoticComponent } from "react";
import { probeConnectivity } from "./connectivity";

/**
 * `React.lazy` that survives a deploy landing under an already-open tab.
 *
 * Routes and big dialogs are code-split, so the browser fetches a page's chunk
 * only when you first navigate to it. Chunk filenames carry a content hash and
 * an Amplify deploy replaces the whole `/static` directory, so a tab that
 * loaded `index-<old>.js` before the deploy asks for `Foo-<old>.js` after it
 * and gets a 404. The browser rejects the dynamic `import()`, and an
 * unhandled rejection unmounts the entire React tree: a white page, no message.
 *
 * In prod (Oct 2026) that landed on /paymentsuccess: Stripe had charged the
 * card, the success page's chunk 404'd, and the page that sends the booking
 * confirmation never mounted — paid, never booked.
 *
 * The reload below is what the user would do by hand — except it happens on its
 * own, before anything broken is on screen. The re-fetched document names the
 * CURRENT entry script, so the chunks it goes on to request are the deployed ones.
 * The URL and the browser-storage booking state survive the reload.
 */

/** Guards against a reload loop. In sessionStorage so it survives the reload. */
export const RELOAD_STAMP_KEY = "_zenvoya_chunk_reload_at";

/**
 * A second reload cannot fix what the first one didn't, so a failure inside this
 * window is treated as "already tried" and allowed to surface as a real error.
 * Long enough to cover the reload round trip, short enough that a genuinely new
 * deploy later in the same session still self-heals.
 */
export const RELOAD_COOLDOWN_MS = 15000;

/**
 * Auto-reload only while the user is still plausibly waiting on the chunk they
 * asked for. React.lazy calls the loader once and never cancels it, so a stalled
 * request (a native import() has no timeout of its own) or a long offline wait can finish
 * after the user has moved on — possibly into Stripe's confirmPayment, where a
 * reload between the charge and the success navigate is paid-but-not-booked
 * again. A stale build 404s in about a second, well inside this window; past
 * it, the error goes to the boundary (or nowhere, if nothing renders it now).
 */
export const AUTO_RELOAD_WINDOW_MS = 20000;

const PROBE_TIMEOUT_MS = 5000;
const OFFLINE_RECHECK_MS = 2500;

/**
 * A missing chunk is reported in more than one shape. Each browser words a
 * failed `import()` differently (Chrome "Failed to fetch dynamically imported
 * module", Firefox "error loading dynamically imported module", Safari
 * "Importing a module script failed"), and Vite's preload helper rejects with
 * "Unable to preload CSS" when a chunk's stylesheet 404s. The webpack shapes
 * (`ChunkLoadError`, `CSS_CHUNK_LOAD_FAILED`) are kept for a tab still running a
 * pre-Vite bundle when this one deploys.
 *
 * Deliberately NOT a catch-all: an import that throws because the module's own
 * top-level code is broken has to reach the error boundary. Reloading for that
 * would fail again identically and hide the real fault.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const { name, code, message } = error as { name?: string; code?: string; message?: string };
  if (name === "ChunkLoadError") return true;
  if (code === "CSS_CHUNK_LOAD_FAILED") return true;
  const text = String(message || "");
  return (
    /Loading (CSS )?chunk\s+\S+\s+failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /error loading dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /Unable to preload CSS/i.test(text)
  );
}

/** sessionStorage throws outright in some privacy modes; it must never be the thing that breaks the app. */
function readReloadStamp(): number {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeReloadStamp(value: number): void {
  try {
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(value));
  } catch {
    /* best effort — the cooldown is a safety net, not a requirement */
  }
}

const isReachable = () => probeConnectivity(PROBE_TIMEOUT_MS);

async function waitUntilReachable(): Promise<void> {
  while (!(await isReachable())) {
    await new Promise((resolve) => setTimeout(resolve, OFFLINE_RECHECK_MS));
  }
}

/**
 * Set once this document has started a reload. A stale boot renders several
 * lazy components at once and all of their chunks 404 together: the first one
 * reloads, the rest must wait on that reload rather than see its fresh stamp,
 * conclude "already reloaded", and flash the error screen.
 */
let reloadStarted = false;

interface LoadChunkDeps {
  probe?: () => Promise<boolean>;
  waitForNetwork?: () => Promise<void>;
  reload?: () => void;
  now?: () => number;
}

/** The retry policy, without React. Callers pass no `deps` and get the real probe, wait, reload and clock. */
export async function loadChunk<T>(
  factory: () => Promise<T>,
  {
    probe = isReachable,
    waitForNetwork = waitUntilReachable,
    reload = () => window.location.reload(),
    now = Date.now,
  }: LoadChunkDeps = {}
): Promise<T> {
  const requestedAt = now();
  try {
    return await factory();
  } catch (error) {
    if (!isChunkLoadError(error)) throw error;

    // One plain retry first. A dropped connection looks identical to a missing
    // chunk from here, and reloading the whole app to recover from a network
    // blip is a heavier answer than the problem deserves. Current browsers re-fetch
    // a module whose fetch failed on the next import(); one that still caches the
    // failure fails again here and falls through to the reload below.
    try {
      return await factory();
    } catch (retryError) {
      if (!isChunkLoadError(retryError)) throw retryError;
    }

    // A chunk that cannot be fetched because there is NO NETWORK is not a stale
    // build — reloading now would swap the app for the browser's offline screen.
    // Wait for the network (the Suspense fallback stays up), then ask again.
    if (!(await probe())) {
      await waitForNetwork();
      try {
        return await factory();
      } catch (afterError) {
        if (!isChunkLoadError(afterError)) throw afterError;
        // Still missing with the network back: a deploy happened. Fall through.
      }
    }

    // Another chunk in this document already triggered the reload: wait on it.
    if (reloadStarted) return new Promise<T>(() => {});

    if (now() - requestedAt > AUTO_RELOAD_WINDOW_MS) {
      // Too long since the user asked for this chunk to reload under them.
      throw error;
    }

    if (now() - readReloadStamp() < RELOAD_COOLDOWN_MS) {
      // Already reloaded and the chunk is STILL missing — a broken build, not a
      // stale tab. Let the error boundary show it; reloading again would loop.
      throw error;
    }

    reloadStarted = true;
    writeReloadStamp(now());
    reload();

    // Never settles, on purpose: the reload is already underway, and resolving
    // here would let React render into a document that is being torn down.
    return new Promise<T>(() => {});
  }
}

export default function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(() => loadChunk(factory));
}
